import {
  AgentPoliciesQuery,
  AgentPolicyInput,
  AgentPolicyRule,
  AuthoredAgentPolicy,
  CreateAgentPolicyRequest,
  isServerScopedRule,
  PolicyVersionConflictError,
  TOOL_CUSTOMIZATION_EFFECTS,
  DeleteAgentPolicyRequest,
  UpdateAgentPolicyRequest,
} from "@/api/temp/AgentPoliciesService";
import { agentEstateStore } from "./store";

/**
 * Demo store for authored agent policies (`AuthoredPolicyApi`). Starts
 * empty: no tool is Customized until an administrator applies a policy.
 *
 * Enforces what the real service will have to - every rule names tools this
 * agent actually reaches on the one MCP server it names (or that whole server,
 * for a `SERVER` rule), a customization never
 * carries a permissive effect, and writes carry the version they were made
 * against - so the UI meets the same failures it will meet live.
 */
/** The policy-level fields; rules are cloned (and given ids) by `validate`. */
const policyFields = ({ rules: _rules, ...fields }: AgentPolicyInput) =>
  structuredClone(fields);

/** An MCP server a rule may name, with its tool ids. */
interface ReachableServer {
  id: string;
  name: string;
  tools: Array<{ id: string }>;
}

class AuthoredAgentPolicyStore {
  private policies: AuthoredAgentPolicy[] = [];
  private sequence = 0;
  /**
   * More servers the agent reaches than its estate record lists - registered
   * by the Access & Protection store, whose services the agent's rules may
   * name. Injected rather than imported: that store already imports this one.
   */
  private extraServers: (
    environmentId: string,
    agentId: string,
  ) => ReachableServer[] = () => [];

  registerServerResolver(
    resolver: (environmentId: string, agentId: string) => ReachableServer[],
  ): void {
    this.extraServers = resolver;
  }

  list({ agentId }: AgentPoliciesQuery): AuthoredAgentPolicy[] {
    return structuredClone(
      this.policies.filter(policy => policy.agentId === agentId),
    );
  }

  create(request: CreateAgentPolicyRequest): AuthoredAgentPolicy {
    const rules = this.validate(request.environmentId, request.agentId, request.policy);
    this.sequence += 1;
    const now = new Date().toISOString();
    const created: AuthoredAgentPolicy = {
      ...policyFields(request.policy),
      rules,
      id: `agent-policy-${request.agentId}-${this.sequence}`,
      agentId: request.agentId,
      status: request.status,
      version: 1,
      updatedAt: now,
      updatedBy: request.actor,
      appliedAt: request.status === "APPLIED" ? now : undefined,
    };
    this.policies = [...this.policies, created];
    return structuredClone(created);
  }

  update(request: UpdateAgentPolicyRequest): AuthoredAgentPolicy {
    const existing = this.policies.find(
      policy =>
        policy.id === request.policyId && policy.agentId === request.agentId,
    );
    if (!existing) throw new Error("This policy no longer exists.");
    if (existing.version !== request.version)
      throw new PolicyVersionConflictError();
    const rules = this.validate(request.environmentId, request.agentId, request.policy);
    const now = new Date().toISOString();
    const updated: AuthoredAgentPolicy = {
      ...existing,
      ...policyFields(request.policy),
      rules,
      status: request.status,
      version: existing.version + 1,
      updatedAt: now,
      updatedBy: request.actor,
      appliedAt:
        request.status === "APPLIED" ? (existing.appliedAt ?? now) : undefined,
    };
    this.policies = this.policies.map(policy =>
      policy.id === updated.id ? updated : policy,
    );
    return structuredClone(updated);
  }

  delete({ agentId, policyId }: DeleteAgentPolicyRequest): { policyId: string } {
    const exists = this.policies.some(
      policy => policy.id === policyId && policy.agentId === agentId,
    );
    if (!exists) throw new Error("This policy no longer exists.");
    this.policies = this.policies.filter(policy => policy.id !== policyId);
    return { policyId };
  }

  /**
   * This agent's policies (drafts included) with a rule naming one MCP server:
   * what blocks removing that server from the agent. A rule must never be left
   * pointing at a server the agent no longer reaches - every later write to
   * its policy would fail validation.
   */
  policiesNamingServer(
    agentId: string,
    mcpServerId: string,
  ): Array<Pick<AuthoredAgentPolicy, "id" | "name">> {
    return this.policies
      .filter(
        policy =>
          policy.agentId === agentId &&
          policy.rules.some(rule => rule.mcpServerId === mcpServerId),
      )
      .map(({ id, name }) => ({ id, name }));
  }

  /** Drop the agent's policies created from suggested improvements (demo reset). */
  removeRecommendationPolicies(agentId: string): void {
    this.policies = this.policies.filter(
      policy => policy.agentId !== agentId || !policy.recommendationId,
    );
  }

  /** Server-side checks; rule ids are kept on edit and minted for new rules. */
  private validate(
    environmentId: string,
    agentId: string,
    policy: AgentPolicyInput,
  ): AgentPolicyRule[] {
    if (!policy.name.trim()) throw new Error("A policy needs a name.");
    if (!policy.rules.length) throw new Error("A policy needs at least one rule.");
    const agent = agentEstateStore.getAgent(environmentId, agentId);
    if (!agent) throw new Error("This agent is not part of the environment.");
    const servers: ReachableServer[] = [
      ...this.extraServers(environmentId, agentId),
      ...agent.mcpServices,
    ];

    return policy.rules.map((rule, index) => {
      const server = servers.find(
        service => service.id === rule.mcpServerId,
      );
      if (!server)
        throw new Error(`Rule ${index + 1} names an MCP server this agent does not reach.`);
      if (isServerScopedRule(rule)) {
        if (policy.origin === "TOOL_CUSTOMIZATION")
          throw new Error("A tool customization targets tools, not a whole server.");
        if (rule.toolIds.length)
          throw new Error(`Rule ${index + 1} targets the whole server and cannot also name tools.`);
      } else if (!rule.toolIds.length)
        throw new Error(`Rule ${index + 1} has no tools selected.`);
      if (rule.toolIds.some(id => !server.tools.some(tool => tool.id === id)))
        throw new Error(`Rule ${index + 1} names a tool that is not on ${server.name}.`);
      if (
        policy.origin === "TOOL_CUSTOMIZATION" &&
        !TOOL_CUSTOMIZATION_EFFECTS.includes(rule.effect)
      )
        throw new Error("A tool customization can only restrict access.");
      this.sequence += 1;
      return {
        ...structuredClone(rule),
        id: rule.id ?? `agent-policy-rule-${this.sequence}`,
      };
    });
  }

  reset(): void {
    this.policies = [];
    this.sequence = 0;
  }
}

export const authoredAgentPolicyStore = new AuthoredAgentPolicyStore();
